import { FunctionComponent, useEffect, useState, ReactNode } from 'react'
import styled from 'styled-components'

import { useGetAdventurePhotosQuery } from '../../hooks/useQueries'

import ActivityMatchedIcon from '../../components/display/ActivityMatchedIcon'
import Avatar from '../../components/display/Avatar'
import GalleryHeader from '../../components/display/GalleryHeader'
import LazyImage from '../../components/display/LazyImage'
import MobileGallery from '../../components/display/MobileGallery'
import Title from '../../components/display/Title'
import ReviewScore from '../../components/reviews/ReviewScore'

import DesktopListingHeader from '../listing/Header/DesktopListingHeader'
import {
  ListingTitle as AdventureTitle,
  ListingWrapper as AdventureWrapper,
} from '../listing/Sections/shared'

import ShowMoreText from '../../components/ShowMoreText'
import { media } from '../../styles/media'

import AdventureMap, { AdventureMapProps } from './AdventureMap'
import Reviews from './Reviews'
import { NarrowerContainer, Section } from './shared'

const MobileHeaderWrapper = styled.div`
  ${media.md`
    display: none;
  `}
`

const Spacer = styled.div`
  margin-bottom: 40px;
`

const SummaryInfo = styled.div`
  color: ${({ theme }) => theme.brand.jungle};
  display: flex;
  justify-content: start;
  flex-wrap: wrap;
  align-items: center;
  font-size: 1.6rem;

  i {
    font-size: 30px;
  }

  & > * {
    margin-bottom: 10px;
  }
`

const ActionsWrapper = styled.div`
  display: flex;

  & > * {
    margin-right: 12px;
  }
`

const AuthorInfoWrapper = styled.div`
  margin-top: 50px;
  margin-bottom: 0;
`

const AuthorInfo = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;

  ${Avatar} {
    display: block;
    min-width: 100px;
    min-height: 100px;
    margin-right: 24px;
  }
`

const DiscoveredBy = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: middle;
  color: #999;

  a {
    color: ${({ theme }) => theme.brand.jungle};
  }
`

interface AdventureProps {
  adventure: AdventureMapProps['adventureData']
  originalListing?: AdventureMapProps['originalListing']
  actionNodes?: ReactNode
  children?: ReactNode
}

const CommonAdventureDetails: FunctionComponent<AdventureProps> = ({
  adventure,
  originalListing,
  actionNodes,
  children,
}) => {
  const [hydrated, setHydrated] = useState(false)
  const [mobileGalleryOpen, setMobileGalleryOpen] = useState(false)

  useEffect(() => {
    setHydrated(true)
  }, [])

  const {
    id,
    adventureType,
    description,
    location,
    reviewScore,
    storyteller,
    storyUrl,
    story,
    title,
  } = adventure

  const { data: photoQueryData } = useGetAdventurePhotosQuery({
    variables: { id: id.toString() },
  })

  const photos = photoQueryData?.adventure?.photos
  const backgroundImage = photos?.[0]?.image

  return (
    <AdventureWrapper>
      <DesktopListingHeader photos={photos || []} />
      <MobileHeaderWrapper>
        <GalleryHeader
          backgroundImage={backgroundImage}
          photos={photos}
          onOpen={() => setMobileGalleryOpen(true)}
        />
        <MobileGallery
          isOpen={mobileGalleryOpen}
          photos={photos}
          onRequestClose={() => setMobileGalleryOpen(false)}
        />
      </MobileHeaderWrapper>
      <NarrowerContainer>
        <AuthorInfoWrapper>
          <AuthorInfo>
            <Avatar>
              <LazyImage data-src={storyteller.avatar.thumb} preload />
            </Avatar>
            <DiscoveredBy>
              <span>Discovered by {storyteller.firstName}</span>
              {story.active && <a href={storyUrl}>{story.title}</a>}
            </DiscoveredBy>
          </AuthorInfo>
        </AuthorInfoWrapper>
        <div style={{ paddingTop: '60px' }}>
          <Title style={{ marginBottom: '20px' }}>
            <AdventureTitle>{title}</AdventureTitle>
            <SummaryInfo>
              <ActivityMatchedIcon
                iconSize="mid"
                iconName={adventureType.slug}
              />
              <span style={{ marginRight: '24px' }}>
                {adventureType.name} in {location.name}
              </span>
              <ReviewScore score={reviewScore} />
            </SummaryInfo>
          </Title>
          <ShowMoreText lines={4} inline={false}>
            {description}
          </ShowMoreText>
          <Spacer />
          <ActionsWrapper>{actionNodes}</ActionsWrapper>
        </div>
      </NarrowerContainer>

      {hydrated && (
        <NarrowerContainer>
          <Section>
            <AdventureMap
              adventureData={adventure}
              originalListing={originalListing}
              photos={photos}
            />
          </Section>
          <Reviews adventureId={id.toString()} score={reviewScore} />
        </NarrowerContainer>
      )}
      {/* Children own their SSR/hydration gating; render them server-side too. */}
      {children}
    </AdventureWrapper>
  )
}

export default CommonAdventureDetails
